import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { errorToastMessage } from '../../../components/layout/toast-root/toast';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useGetExerciseProperties } from '../../../redux/api/exerciseConfigAPI';
import { useAiTraining } from '../../../redux/api/trainingsAPI';
import { ExercisePropertyType } from '../../../shared/constants';
import { Paths } from '../../../shared/paths';
import { IExerciseProperty } from '../../../types/interfaces/exercise-property';
import { getGenerateTrainingValidation } from './FormikData';

const DEFAULT_TRAINING_DURATION_SECONDS = 60 * 60 * 2;
const DEFAULT_REST_DURATION_SECONDS = 60;
const AI_GENERATION_APP_LIMIT_EXCEEDED = 'AI_GENERATION_APP_LIMIT_EXCEEDED';

type TApiErrorLike = {
  code?: string;
  data?: { code?: string };
};

const isAiGenerationLimitExceededError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const parsedError = error as TApiErrorLike;
  return (
    parsedError.code === AI_GENERATION_APP_LIMIT_EXCEEDED ||
    parsedError.data?.code === AI_GENERATION_APP_LIMIT_EXCEEDED
  );
};

export function useGenerateTraining() {
  const { t } = useTranslation();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [criteriaSubmitError, setCriteriaSubmitError] = useState<string | null>(
    null,
  );

  const { data: properties } = useGetExerciseProperties();
  const muscleGroups = useMemo(
    () =>
      properties?.filter((p) => p.type === ExercisePropertyType.muscleGroup) ||
      [],
    [properties],
  );
  const categories = useMemo(
    () =>
      properties?.filter((p) => p.type === ExercisePropertyType.category) || [],
    [properties],
  );
  const equipments = useMemo(
    () =>
      properties?.filter((p) => p.type === ExercisePropertyType.equipment) ||
      [],
    [properties],
  );
  const developedSkillOptions = useMemo(
    () =>
      properties?.filter(
        (p) => p.type === ExercisePropertyType.developedSkill,
      ) || [],
    [properties],
  );

  const { navigate } = useNavigationStack();
  const [aiTraining, { isLoading }] = useAiTraining();

  const formik = useFormik({
    initialValues: {
      prompt: '',
      mainMuscleGroups: [] as IExerciseProperty[],
      categories: [] as IExerciseProperty[],
      equipments: [] as IExerciseProperty[],
      developedSkills: [] as IExerciseProperty[],
      trainingDurationSeconds: DEFAULT_TRAINING_DURATION_SECONDS,
      restDurationSeconds: DEFAULT_REST_DURATION_SECONDS,
    },
    validationSchema: getGenerateTrainingValidation(t),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setSubmitError(null);
      const criteriaTotal =
        (values.mainMuscleGroups?.length ?? 0) +
        (values.categories?.length ?? 0) +
        (values.equipments?.length ?? 0) +
        (values.developedSkills?.length ?? 0);
      if (criteriaTotal < 1) {
        setCriteriaSubmitError(t('trainings.generateAtLeastOneCriterion'));
        return;
      }
      setCriteriaSubmitError(null);
      aiTraining({
        prompt: values.prompt?.trim() ?? '',
        muscleGroupIds:
          values.mainMuscleGroups?.length > 0
            ? values.mainMuscleGroups.map((m) => m.id)
            : undefined,
        categoryIds:
          values.categories?.length > 0
            ? values.categories.map((c) => c.id)
            : undefined,
        equipmentIds:
          values.equipments?.length > 0
            ? values.equipments.map((eq) => eq.id)
            : undefined,
        developedSkillIds:
          values.developedSkills?.length > 0
            ? values.developedSkills.map((s) => s.id)
            : undefined,
      })
        .unwrap()
        .then((res) => {
          if (res.id) {
            navigate(Paths.trainings.toViewId(res.id), { replace: true });
          }
        })
        .catch((error) => {
          if (isAiGenerationLimitExceededError(error)) {
            errorToastMessage(t('trainings.generateLimitExceededErrorMessage'));
            return;
          }
          setSubmitError(t('trainings.generateErrorMessage'));
        });
    },
  });

  const criteriaChangeKey = useMemo(() => {
    const ids = (list: IExerciseProperty[] | undefined) =>
      (list ?? []).map((x) => x.id).join(',');
    return [
      ids(formik.values.mainMuscleGroups),
      ids(formik.values.categories),
      ids(formik.values.equipments),
      ids(formik.values.developedSkills),
    ].join('|');
  }, [
    formik.values.mainMuscleGroups,
    formik.values.categories,
    formik.values.equipments,
    formik.values.developedSkills,
  ]);

  useEffect(() => {
    setCriteriaSubmitError(null);
  }, [criteriaChangeKey]);

  return {
    formik,
    submitError,
    criteriaSubmitError,
    mainMuscleGroups: muscleGroups,
    categories,
    equipments,
    developedSkillOptions,
    isLoading,
  };
}
